import { useMutation, useQuery } from '@tanstack/react-query'
import type { PermissaoDoConector } from '@orbind/core'
import { api } from '../../lib/api.ts'

/**
 * Assistente com IA (SPEC-20261104-2229). A conversa vive na TELA: o histórico
 * que a API devolve (blocos da Messages API, opacos aqui) vai de volta inteiro
 * na mensagem seguinte. Recarregar a página começa outra conversa.
 */

export type SituacaoDoAssistente = {
  configurado: boolean
  noPlano: boolean
  liberado: boolean
  permissoes: PermissaoDoConector[]
}

/** Bloco do histórico — a tela não lê, só devolve. */
export type MensagemDoHistorico = { role: 'user' | 'assistant'; content: string | Record<string, unknown>[] }

/** Resultado de ferramenta guardado enquanto a pergunta espera a escolha — opaco aqui. */
export type ResultadoPendente = { type: 'tool_result'; tool_use_id: string; content: string; is_error?: boolean }

/** Pergunta de decisão: opções clicáveis, a recomendada primeiro. */
export type PerguntaDoAssistente = {
  id: string
  pergunta: string
  opcoes: { rotulo: string; descricao: string | null; recomendada: boolean }[]
  motivo: string
  multipla: boolean
  pendentes: ResultadoPendente[]
}

export type RespostaDoAssistente = {
  historico: MensagemDoHistorico[]
  texto: string
  acoes: { ferramenta: string; erro: boolean }[]
  pergunta: PerguntaDoAssistente | null
}

export type PedidoAoAssistente = {
  historico: MensagemDoHistorico[]
  mensagem?: string
  resposta?: { pergunta: string; resposta: string; pendentes: ResultadoPendente[] }
}

async function lerErro(resposta: Response, padrao: string): Promise<never> {
  const corpo = (await resposta.json().catch(() => ({}))) as { error?: { message?: string } }
  throw new Error(corpo.error?.message ?? padrao)
}

export function useSituacaoDoAssistente() {
  return useQuery({
    queryKey: ['ai-assistant', 'me'],
    queryFn: async () => {
      const r = await api['ai-assistant'].me.$get()
      if (!r.ok) await lerErro(r, 'Não foi possível carregar o Orbind AI.')
      return (await r.json()) as SituacaoDoAssistente
    },
  })
}

export function useEnviarAoAssistente() {
  return useMutation({
    mutationFn: async (pedido: PedidoAoAssistente) => {
      const r = await api['ai-assistant'].chat.$post({ json: pedido })
      if (!r.ok) await lerErro(r, 'O Orbind AI não conseguiu responder agora.')
      return (await r.json()) as RespostaDoAssistente
    },
  })
}

/** Nome que a pessoa entende para cada ferramenta, no rodapé da resposta. */
export const ROTULO_DA_FERRAMENTA: Record<string, string> = {
  meus_itens: 'consultou seus Itens',
  buscar_itens: 'buscou Itens',
  ver_item: 'abriu um Item',
  catalogos: 'consultou os catálogos',
  resumo_do_dia: 'montou o resumo do dia',
  evolucao_dos_itens: 'consultou a evolução',
  atualizar_item: 'alterou um Item',
  mover_para_etapa: 'moveu um Item de etapa',
  adicionar_checklist: 'mexeu num checklist',
  comentar_item: 'comentou num Item',
  concluir_item: 'concluiu um Item',
  criar_item: 'criou um Item',
  criar_campo_complementar: 'criou um campo complementar',
}
